/**
 * Post-sale reservation operations — the legacy app's `ShopCartController` actions,
 * ported into the API (backend Faz A). The shared `ReservationDetailPage` wires three of
 * them, split by record state (see components/reservation/capabilities.ts):
 *  - a still-`Prebooked` *reservation* → `ShoppingCart/EditOptionAndPrice` (+ İptal Et,
 *    which is `Air/CancelReservation`, in api/endpoints/air.ts);
 *  - an issued *ticket* (`Booked`/`Void`/`Refund`) → the generic `/Void`
 *    (api/endpoints/shoppingCart.ts, `voidShoppingCart`) and `ShoppingCart/Refund`.
 * SA-3 (2026-09-07): the Open/Change/Edit/AddNote and RefundCancel request types are now
 * modeled (from `Docs/api/04-commerce-payments.md` §ShoppingCartController — code-traced
 * `OpenRequest`/`ChangeRequest`/`EditRequest`/`NoteRequest`/`RefundCancelRequest`) and the
 * wrappers exist in api/endpoints/postSale.ts, but there is NO UI: live testing showed the
 * backend `OpenOrChange`/`Edit` skeleton mutates the cart + ticketing DB unconditionally
 * (no business-rule guard), so the disabled "Bilet İşlemleri" card was removed
 * (2026-09-08). See docs/backend-requirements.md §N-1. `AddNote` is comparatively safe
 * (append a line to `cart.Notes`) but also has no UI right now.
 * `PostSaleResponse` describes what `EditOptionAndPrice` / `Refund` return:
 * `{ shoppingCart, successMessage }` on success, `{ hasError, serviceError }` on a
 * business-rule rejection. The audit trail rides inside `shoppingCart.processHistories`.
 */
import type { ResponseBase } from './common';
import type { ShoppingCart } from './shoppingCart';

/** The cart as returned by a post-sale route — same shape as `GetShoppingCart`'s, but
 *  we lean on `processHistories` here so name the field explicitly. */
export type PostSaleCart = ShoppingCart & {
  processHistories?: ProcessHistoryEntry[] | null;
};

/** Common envelope every post-sale route returns: the mutated cart + a confirmation
 *  message. `shoppingCart.processHistories` is the operation audit trail. */
export interface PostSaleResponse extends ResponseBase {
  shoppingCart?: PostSaleCart | null;
  /** Human-readable confirmation, e.g. "SKRRPH opsiyon güncellendi." */
  successMessage?: string | null;
}

/**
 * One row of the reservation's operation audit trail. CONFIRMED live 2026-08-27:
 * `{ processId, date, processHistoryType, explanation, username, bookingId, paxId }`
 * (`date` is a `dd.MM.yyyy HH:mm:ss` string; there is no monetary column — amounts are
 * embedded in `explanation`).
 */
export interface ProcessHistoryEntry {
  processId?: string | null;
  date?: string;
  /** Operation kind — REFUND / REFUNDCANCELLED / VOID / OPEN / EDIT / EDITED /
   *  AIRBOOKING / AIRPREBOOKING / PASSENGERUPDATED … */
  processHistoryType?: string;
  explanation?: string;
  /** Operator who performed the action. */
  username?: string;
  bookingId?: string;
  paxId?: string;
}

export interface OptionPriceBreakdown {
  paxReferanceId?: string;
  baseFare?: number;
  totalTax?: number;
  totalFare?: number;
}

/** Legacy `EditOptionAndPrice` — adjust an offered reservation's option expiry + price. */
export interface EditOptionAndPriceRequest {
  shoppingCartId: string;
  pnr?: string;
  expireDate?: string;
  expireTime?: string;
  breakdowns?: OptionPriceBreakdown[];
}

/**
 * `ShoppingCart/Refund` refund direction (`RefundType` in the backend, serialised as its
 * ordinal — 0/1/2). Legacy labels: "Gidiş" / "Dönüş" / "Hepsi".
 */
export const REFUND_TYPES = [
  { value: 0, label: 'Gidiş' },
  { value: 1, label: 'Dönüş' },
  { value: 2, label: 'Hepsi' },
] as const;

export type RefundType = (typeof REFUND_TYPES)[number]['value'];

/**
 * `ShoppingCart/Refund` request — mirrors the backend `RefundRequest`. `paxId`/`bookingId`
 * are the cart's GUIDs, resolved from `GetShoppingCart` by matching the ticket number
 * (the flat `Entry/Find` row doesn't carry them). The amount fields are the refund
 * breakdown the operator enters; the legacy UI computes
 * `total = baseFare + vq + yr + serviceFee − extraServiceFee`. `total: 0` triggers a
 * book-only refund (no GDS call — just flags the breakdown + appends an audit row).
 * The backend enforces `baseFare ≤ breakdown.baseFare`, `vq + yr ≤ breakdown.totalTax`,
 * `serviceFee ≤ breakdown.totalServiceFee`, `total ≤ remaining fare` and returns a
 * Turkish message on any breach.
 */
export interface RefundRequest {
  shoppingCartId: string;
  paxId: string;
  bookingId: string;
  pnr?: string;
  ticket?: string;
  description?: string;
  baseFare: number;
  vq: number;
  yr: number;
  debit: number;
  serviceFee: number;
  extraServiceFee: number;
  total: number;
  refundType: RefundType;
}

/* ---------------------------------------------------------------------------
 * SA-3 — destructive `ShoppingCart/*` post-sale ops. BACKEND SKELETON: the worker's
 * shared `OpenOrChange` / `Edit` path mutates the Mongo cart + Arkman ticketing DB with
 * no business-rule guard, so there is no UI — the disabled placeholder card was removed
 * 2026-09-08 (docs/backend-requirements.md §N-1). Shapes are code-traced from
 * `Docs/api/04-commerce-payments.md` §ShoppingCartController; `BookingId` / `PaxId` are
 * the cart's GUIDs (strings on the wire).
 * ------------------------------------------------------------------------- */

/** `ShoppingCart/Open` — put an issued ticket "on request / open". `penalty + serviceFee
 *  > 0` writes a new ticket record + payment history; a new `newPnr` spawns a new
 *  `AirBooking`. `posCode` empty → `CASH` payment history, else `CREDITCARD`. */
export interface OpenRequest {
  shoppingCartId: string;
  bookingId: string;
  paxId: string;
  originalPnr?: string;
  newPnr?: string;
  ticket?: string;
  penalty?: number;
  serviceFee?: number;
  description?: string;
  /** `"Rt"` = round-trip (two change-history rows); else a leg code. */
  route?: string;
  posCode?: string;
  lastDepartureDateTime?: string;
}

/** `ShoppingCart/Change` — ticket exchange / reissue. Same fields as `OpenRequest` plus
 *  a return-leg departure for round-trip changes. */
export interface ChangeRequest extends OpenRequest {
  returnDepartureDateTime?: string;
}

/** `ShoppingCart/Edit` — amend the fee / service-fee / POS of an existing post-sale
 *  process identified by `processId` (must match a process *and* a payment-history row). */
export interface EditRequest {
  shoppingCartId: string;
  bookingId: string;
  paxId: string;
  processId: string;
  ticket?: string;
  penalty?: number;
  serviceFee?: number;
  posCode?: string;
}

/** `ShoppingCart/AddNote` — append a free-text line to `ShoppingCart.Notes`. The least
 *  destructive of the set (no ticketing-DB call). */
export interface NoteRequest {
  shoppingCartId: string;
  note: string;
}

/** `ShoppingCart/RefundCancel` — reverse a previously recorded refund identified by
 *  `processId` (calls `TicketService.RefundCancel` with the stored `RefundInfo.Order`). */
export interface RefundCancelRequest {
  shoppingCartId: string;
  processId: string;
  paxId: string;
  bookingId: string;
}

/* ---------------------------------------------------------------------------
 * SA-3 — Arkman ticket import (`arkman/start-ticket-import`, Swagger-hidden). Response is
 * envelope-only (`TicketImportResponse : ResponseBase`, empty body). Triggered per
 * ticket number from the "Bilet İçe Aktarma" section at the bottom of the ticket-detail
 * page (components/reservation/TicketImportSection.tsx).
 * ------------------------------------------------------------------------- */

export interface TicketImportRequest {
  shoppingCartId: string;
  /** the `AirBooking.id` GUID (`AirBookingId` on the wire). */
  airBookingId: string;
  /** must match a `PassengerBreakdown.TicketNumber` on that booking. */
  ticketNumber: string;
}
